import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AppLayout from './layouts/AppLayout';
import PublicLayout from './layouts/PublicLayout';
import RouteGuard from './components/RouteGuard';
import PhaseGate from './components/PhaseGate';

// Public Pages
import Homepage from './pages/Homepage';
import About from './pages/public/About';
import Contact from './pages/public/Contact';
import Plans from './pages/public/Plans';
import FAQs from './pages/public/FAQs';
import Terms from './pages/public/Terms';
import Privacy from './pages/public/Privacy';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import Verify from './pages/auth/Verify';
import Forgot from './pages/auth/Forgot';

// Dashboard Pages
import FounderDashboard from './pages/dashboards/FounderDashboard';
import MentorDashboard from './pages/dashboards/MentorDashboard';
import AdminDashboard from './pages/dashboards/AdminDashboard';
import InvestorDashboard from './pages/investor/InvestorDashboard';


// LMS Pages
import Courses from './pages/lms/Courses';
import CourseDetail from './pages/lms/CourseDetail';
import Lesson from './pages/lms/Lesson';
import Quiz from './pages/lms/Quiz';
import QuizResult from './pages/lms/QuizResult';
import Certificates from './pages/lms/Certificates';

// Session Pages
import GroupSessions from './pages/sessions/GroupSessions';

// Pitch Pages
import IdeaSubmission from './pages/pitch/IdeaSubmission';
import PitchTemplate from './pages/pitch/PitchTemplate';
import PitchReview from './pages/pitch/PitchReview';
import FinalPitchReview from './pages/pitch/FinalPitchReview';
import InitialPitch from './pages/pitch/InitialPitch';
import FinalPitch from './pages/pitch/FinalPitch';

// Startup Pages
import Apply from './pages/startup/Apply';
import MyStartup from './pages/startup/MyStartup';
import PitchDeck from './pages/startup/PitchDeck';
import StartupPublic from './pages/startup/StartupPublic';
import StartupAdmin from './pages/startup/StartupAdmin';
import StartupJourney from './pages/startup/StartupJourney';

// Investor Pages
import PitchView from './pages/investor/PitchView';

// Partner Pages
import PartnerDashboard from './pages/dashboards/PartnerDashboard';
import PartnerStartupDetail from './pages/partner/PartnerStartupDetail';

// Associate Pages
import AssociateDashboard from './pages/dashboards/AssociateDashboard';
import AssociateMentorProfile from './pages/associate/AssociateMentorProfile';

// Admin Pages
import MentorManager from './pages/admin/MentorManager';
import Users from './pages/admin/Users';
import AdminCourses from './pages/admin/AdminCourses';
import CourseBuilder from './pages/admin/CourseBuilder';
import PhaseConfig from './pages/admin/PhaseConfig';
import GroupSessionScheduler from './pages/admin/GroupSessionScheduler';
import InvestorManager from './pages/admin/InvestorManager';
import Applications from './pages/admin/Applications';

// Analytics Pages
import Analytics from './pages/analytics/Analytics';

// System Pages
import Settings from './pages/system/Settings';
import NotFound from './pages/system/NotFound';
import Forbidden from './pages/system/Forbidden';
import ServerError from './pages/system/ServerError';
import Pending from './pages/system/Pending';
import AuthAction from './pages/auth/AuthAction';
import FounderPlans from './pages/auth/Plans';
import CompleteProfile from './pages/auth/CompleteProfile';
import InviteAccept from './pages/auth/InviteAccept';
import AdminLogin from './pages/auth/AdminLogin';

const AppRouter = () => {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Homepage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/faqs" element={<FAQs />} />
        <Route path="/legal/terms" element={<PublicLayout><Terms /></PublicLayout>} />
        <Route path="/legal/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />

        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify" element={<PublicLayout><Verify /></PublicLayout>} />
        <Route path="/forgot" element={<PublicLayout><Forgot /></PublicLayout>} />
        <Route path="/pending" element={<Pending />} />
        <Route path="/auth/action" element={<AuthAction />} />
        <Route path="/select-plan" element={<FounderPlans />} />
        <Route path="/complete-profile" element={<CompleteProfile />} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="/admin" element={<AdminLogin />} />

        {/* App Routes (with sidebar) */}
        <Route path="/app" element={<AppLayout />}>
          {/* Dashboards */}
          <Route path="dashboard/founder" element={<RouteGuard allowedRoles={['founder']}><FounderDashboard /></RouteGuard>} />
          <Route path="dashboard/mentor" element={<RouteGuard allowedRoles={['mentor']}><MentorDashboard /></RouteGuard>} />
          <Route path="dashboard/admin" element={<RouteGuard allowedRoles={['admin']}><AdminDashboard /></RouteGuard>} />
          <Route path="dashboard/investor" element={<RouteGuard allowedRoles={['investor']}><InvestorDashboard /></RouteGuard>} />
          <Route path="dashboard/partner" element={<RouteGuard allowedRoles={['partner']}><PartnerDashboard /></RouteGuard>} />
          <Route path="partner/startup/:id" element={<RouteGuard allowedRoles={['partner']}><PartnerStartupDetail /></RouteGuard>} />
          <Route path="dashboard/associate" element={<RouteGuard allowedRoles={['associate']}><AssociateDashboard /></RouteGuard>} />
          <Route path="associate/startup/:id" element={<RouteGuard allowedRoles={['associate']}><PartnerStartupDetail /></RouteGuard>} />
          <Route path="associate/mentor/:id" element={<RouteGuard allowedRoles={['associate']}><AssociateMentorProfile /></RouteGuard>} />

          {/* LMS */}
          <Route path="lms/courses" element={<RouteGuard allowedRoles={['founder', 'admin']}><Courses /></RouteGuard>} />
          <Route path="lms/courses/:id" element={<RouteGuard allowedRoles={['founder', 'admin']}><CourseDetail /></RouteGuard>} />
          <Route path="lms/lesson/:id" element={<RouteGuard allowedRoles={['founder', 'admin']}><Lesson /></RouteGuard>} />
          <Route path="lms/quiz/:id" element={<RouteGuard allowedRoles={['founder', 'admin']}><Quiz /></RouteGuard>} />
          <Route path="lms/quiz/:id/result" element={<RouteGuard allowedRoles={['founder', 'admin']}><QuizResult /></RouteGuard>} />
          <Route path="lms/certificates" element={<RouteGuard allowedRoles={['founder', 'admin']}><Certificates /></RouteGuard>} />

          {/* Group Sessions */}
          <Route path="sessions" element={<RouteGuard allowedRoles={['founder', 'mentor', 'admin']}><GroupSessions /></RouteGuard>} />

          {/* Pitch */}
          <Route path="pitch/idea" element={<RouteGuard allowedRoles={['founder']}><PhaseGate phaseType="pitching"><IdeaSubmission /></PhaseGate></RouteGuard>} />
          <Route path="pitch/prepare" element={<RouteGuard allowedRoles={['founder']}><PhaseGate phaseType="pitching"><PitchTemplate /></PhaseGate></RouteGuard>} />
          <Route path="pitch/review/:id" element={<RouteGuard allowedRoles={['mentor']}><PitchReview /></RouteGuard>} />
          <Route path="pitch/final-review/:id" element={<RouteGuard allowedRoles={['mentor', 'admin']}><FinalPitchReview /></RouteGuard>} />
          <Route path="pitch/initial" element={<RouteGuard allowedRoles={['founder']}><InitialPitch /></RouteGuard>} />
          <Route path="pitch/final" element={<RouteGuard allowedRoles={['founder']}><FinalPitch /></RouteGuard>} />

          {/* Startup */}
          <Route path="startup/apply" element={<RouteGuard allowedRoles={['founder']}><Apply /></RouteGuard>} />
          <Route path="startup" element={<RouteGuard allowedRoles={['founder']}><MyStartup /></RouteGuard>} />
          <Route path="startup/journey" element={<RouteGuard allowedRoles={['founder']}><StartupJourney /></RouteGuard>} />
          <Route path="startup/pitch" element={<RouteGuard allowedRoles={['founder']}><PitchDeck /></RouteGuard>} />
          <Route path="startup/public/:id" element={<StartupPublic />} />
          <Route path="admin/startup/:id" element={<RouteGuard allowedRoles={['admin']}><StartupAdmin /></RouteGuard>} />

          {/* Investor */}
          <Route path="investor/pitches/:id" element={<RouteGuard allowedRoles={['investor']}><PitchView /></RouteGuard>} />
          <Route path="investor/startup/:id" element={<RouteGuard allowedRoles={['investor']}><StartupPublic /></RouteGuard>} />

          {/* Admin */}
          <Route path="admin/mentors" element={<RouteGuard allowedRoles={['admin']}><MentorManager /></RouteGuard>} />
          <Route path="admin/users" element={<RouteGuard allowedRoles={['admin']}><Users /></RouteGuard>} />
          <Route path="admin/courses" element={<RouteGuard allowedRoles={['admin']}><AdminCourses /></RouteGuard>} />
          <Route path="admin/courses/new" element={<RouteGuard allowedRoles={['admin']}><CourseBuilder /></RouteGuard>} />
          <Route path="admin/courses/:id/edit" element={<RouteGuard allowedRoles={['admin']}><CourseBuilder /></RouteGuard>} />
          <Route path="admin/phases" element={<RouteGuard allowedRoles={['admin']}><PhaseConfig /></RouteGuard>} />
          <Route path="admin/sessions" element={<RouteGuard allowedRoles={['admin']}><GroupSessionScheduler /></RouteGuard>} />
          <Route path="admin/investors" element={<RouteGuard allowedRoles={['admin']}><InvestorManager /></RouteGuard>} />
          <Route path="admin/applications" element={<RouteGuard allowedRoles={['admin']}><Applications /></RouteGuard>} />

          {/* Analytics */}
          <Route path="analytics" element={<RouteGuard allowedRoles={['admin']}><Analytics /></RouteGuard>} />

          {/* System */}
          <Route path="settings" element={<RouteGuard allowedRoles={['founder', 'mentor', 'admin', 'investor', 'partner', 'associate']}><Settings /></RouteGuard>} />
        </Route>

        {/* Error Pages */}
        <Route path="/403" element={<Forbidden />} />
        <Route path="/500" element={<ServerError />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
};

export default AppRouter;
